import React, { useState, useRef } from "react";
import { X, Upload, FileText, Check, Warning, ArrowRight, Trash, Info } from "phosphor-react";
import { Button } from "@/components/ui/button";
import api from "../lib/api";
import { toast } from "sonner";

export default function ImportAnalyticsModal({ isOpen, onClose, onImportSuccess }) {
  const [step, setStep] = useState(1); // 1: upload, 2: preview, 3: importing, 4: result
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showTemplate, setShowTemplate] = useState(false);
  const [importName, setImportName] = useState(''); // Name for this import batch
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  // Normalize values according to schema rules
  const normalizeRow = (row) => {
    return {
      direction: (row.direction || '').trim().toLowerCase(),
      outcome: (row.outcome || '').trim().toLowerCase(),
      rr: parseFloat(row.rr) || 0,
      timeframe: (row.timeframe || '').trim().toUpperCase(),
      // Setup: trim + title case, or "No setup" if empty
      setup: (row.setup && row.setup.trim()) 
        ? row.setup.trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
        : 'No setup'
    };
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }

    setFile(selectedFile);
    parseCSV(selectedFile);
  };

  const parseCSV = (csvFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          toast.error("CSV must have at least a header row and one data row");
          return;
        }

        // Parse header
        const headerLine = lines[0];
        const headers = headerLine.split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
        
        // Required columns (5 fields only)
        const requiredColumns = ['direction', 'outcome', 'rr', 'timeframe'];
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        
        if (missingColumns.length > 0) {
          toast.error(`Missing required columns: ${missingColumns.join(', ')}`);
          setParseErrors([`Missing required columns: ${missingColumns.join(', ')}`]);
          return;
        }

        // Parse data rows
        const rows = [];
        const errors = [];
        
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          // Simple CSV parsing (handles basic cases)
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          
          if (values.length < headers.length) {
            errors.push(`Row ${i}: Incomplete data`);
            continue;
          }
          
          const row = {};
          headers.forEach((header, idx) => {
            row[header] = values[idx] || '';
          });
          
          // Convert RR to number
          if (row.rr) {
            const rrNum = parseFloat(row.rr);
            if (isNaN(rrNum)) {
              errors.push(`Row ${i}: Invalid RR value '${row.rr}'`);
              continue;
            }
            row.rr = rrNum;
          }
          
          // Normalize and build clean row with only 5 approved fields
          const cleanRow = normalizeRow(row);
          
          // Validate normalized values
          if (!['long', 'short'].includes(cleanRow.direction)) {
            errors.push(`Row ${i}: Invalid direction '${row.direction}'`);
            continue;
          }
          
          if (!['win', 'loss', 'breakeven'].includes(cleanRow.outcome)) {
            errors.push(`Row ${i}: Invalid outcome '${row.outcome}'`);
            continue;
          }
          
          if (!cleanRow.timeframe) {
            errors.push(`Row ${i}: Missing timeframe`);
            continue;
          }
          
          rows.push(cleanRow);
        }
        
        setParsedRows(rows);
        setParseErrors(errors);
        setStep(2); // Move to preview
        
      } catch (err) {
        toast.error("Error parsing CSV file");
        console.error("CSV parse error:", err);
      }
    };
    reader.readAsText(csvFile);
  };

  const handleImport = async () => {
    if (parsedRows.length === 0) {
      toast.error("No valid rows to import");
      return;
    }
    
    if (!importName || importName.trim() === '') {
      toast.error("Please enter a name for this import");
      return;
    }

    setImporting(true);
    setStep(3);

    try {
      const response = await api.post('/analytics/import', {
        rows: parsedRows,
        import_name: importName.trim()
      });

      setImportResult(response.data);
      setStep(4);
      
      if (response.data.imported_count > 0) {
        toast.success(`Imported ${response.data.imported_count} trades to "${importName}"`);
        if (onImportSuccess) {
          onImportSuccess();
        }
      }
      
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Failed to import data");
      setStep(2); // Go back to preview
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setParsedRows([]);
    setParseErrors([]);
    setImportResult(null);
    setImportName('');
    onClose();
  };

  const handleReset = () => {
    setStep(1);
    setFile(null);
    setParsedRows([]);
    setParseErrors([]);
    setImportResult(null);
    setImportName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="text-xl font-bold font-heading text-white">Import Analytics Data</h2>
            <p className="text-white/50 text-sm mt-1">Import historical trades for analytics visualization only</p>
          </div>
          <button
            onClick={handleClose}
            className="text-white/40 hover:text-white transition-colors"
          >
            <X size={24} weight="bold" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Info banner */}
              <div className="bg-[#00B8D4]/5 border border-[#00B8D4]/20 rounded-sm p-4">
                <div className="flex items-start gap-3">
                  <Info size={20} className="text-[#00B8D4] shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-white/80 font-medium mb-1">Analytics-only import</p>
                    <p className="text-white/50">
                      Imported data is used for high-level analytics visualization only. 
                      It does not affect Trade History, Open Positions, Insights, or Advanced Analytics.
                    </p>
                  </div>
                </div>
              </div>

              {/* Upload area */}
              <label
                className="border-2 border-dashed border-white/10 rounded-sm p-8 flex flex-col items-center justify-center cursor-pointer hover:border-white/20 transition-colors"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload size={40} weight="duotone" className="text-white/40 mb-3" />
                <p className="text-white/60 text-center mb-2">Click to upload CSV file</p>
                <p className="text-white/30 text-xs text-center">Maximum file size: 5MB</p>
              </label>

              {/* CSV format guide */}
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                <p className="text-white/60 text-xs font-mono uppercase tracking-wider mb-3">Required CSV columns</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-white/40">•</span>
                    <span className="text-white/70 font-mono">direction</span>
                    <span className="text-white/30 text-xs">(long/short)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/40">•</span>
                    <span className="text-white/70 font-mono">outcome</span>
                    <span className="text-white/30 text-xs">(win/loss/breakeven)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/40">•</span>
                    <span className="text-white/70 font-mono">rr</span>
                    <span className="text-white/30 text-xs">(e.g. 2.5, -1.0)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/40">•</span>
                    <span className="text-white/70 font-mono">timeframe</span>
                    <span className="text-white/30 text-xs">(e.g. 5M, 1H, 4H)</span>
                  </div>
                </div>
                <p className="text-white/40 text-xs mt-3">
                  Optional: <span className="font-mono">setup</span> (defaults to "No setup" if empty)
                </p>
                
                {/* Example format trigger */}
                <button
                  type="button"
                  onClick={() => setShowTemplate(true)}
                  className="mt-3 text-[#00B8D4] hover:text-[#00B8D4]/80 text-sm transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <FileText size={14} weight="bold" />
                  View example format
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText size={24} weight="duotone" className="text-[#00E676]" />
                  <div>
                    <p className="text-white font-medium">{file?.name}</p>
                    <p className="text-white/50 text-sm">{parsedRows.length} valid rows found</p>
                  </div>
                </div>
                <button
                  onClick={handleReset}
                  className="text-white/40 hover:text-white text-sm flex items-center gap-1"
                >
                  <Trash size={14} />
                  Remove
                </button>
              </div>

              {/* Parse errors */}
              {parseErrors.length > 0 && (
                <div className="bg-[#FFC107]/5 border border-[#FFC107]/20 rounded-sm p-3">
                  <p className="text-[#FFC107] text-sm font-medium mb-2">
                    {parseErrors.length} row(s) will be skipped:
                  </p>
                  <ul className="text-white/50 text-xs space-y-1 max-h-20 overflow-y-auto">
                    {parseErrors.slice(0, 5).map((err, i) => (
                      <li key={i}>• {err}</li>
                    ))}
                    {parseErrors.length > 5 && (
                      <li>... and {parseErrors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Import Name Input - REQUIRED */}
              <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                <label className="block text-white/60 text-sm mb-2">
                  Name this import <span className="text-[#FF3B30]">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Binance Jan 2025, Backtest BTC, TradingView Export..."
                  value={importName}
                  onChange={(e) => setImportName(e.target.value)}
                  className="w-full px-3 py-2.5 bg-[#121212] border border-white/10 rounded-sm text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-white/30 transition-colors"
                  maxLength={50}
                />
                <p className="text-white/30 text-xs mt-2">
                  Use a descriptive name to easily identify this batch later
                </p>
              </div>

              {/* Preview table */}
              <div className="border border-white/10 rounded-sm overflow-hidden">
                <div className="bg-[#0A0A0A] px-3 py-2 border-b border-white/10">
                  <p className="text-white/40 text-xs font-mono uppercase tracking-wider">Preview (first 5 rows)</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#0A0A0A]/50">
                        <th className="px-3 py-2 text-left text-white/40 font-mono text-xs">Direction</th>
                        <th className="px-3 py-2 text-left text-white/40 font-mono text-xs">Outcome</th>
                        <th className="px-3 py-2 text-left text-white/40 font-mono text-xs">RR</th>
                        <th className="px-3 py-2 text-left text-white/40 font-mono text-xs">Timeframe</th>
                        <th className="px-3 py-2 text-left text-white/40 font-mono text-xs">Setup</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className={`px-3 py-2 ${row.direction === 'long' ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                            {row.direction}
                          </td>
                          <td className={`px-3 py-2 ${
                            row.outcome === 'win' ? 'text-[#00E676]' : 
                            row.outcome === 'loss' ? 'text-[#FF3B30]' : 'text-white/50'
                          }`}>
                            {row.outcome}
                          </td>
                          <td className="px-3 py-2 text-white/70 font-mono">{row.rr}</td>
                          <td className="px-3 py-2 text-white/50 font-mono">{row.timeframe}</td>
                          <td className="px-3 py-2 text-white/50">{row.setup}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {parsedRows.length > 5 && (
                <p className="text-white/40 text-xs text-center">
                  ... and {parsedRows.length - 5} more rows
                </p>
              )}
            </div>
          )}

          {/* Step 3: Importing */}
          {step === 3 && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 border-2 border-white/20 border-t-[#00E676] rounded-full animate-spin mb-4"></div>
              <p className="text-white/70">Importing {parsedRows.length} trades...</p>
              <p className="text-white/40 text-sm mt-1">This may take a moment</p>
            </div>
          )}

          {/* Step 4: Result */}
          {step === 4 && importResult && (
            <div className="space-y-4">
              {/* Success/Warning banner */}
              <div className={`rounded-sm p-4 ${
                importResult.imported_count > 0 
                  ? 'bg-[#00E676]/10 border border-[#00E676]/20' 
                  : 'bg-[#FF3B30]/10 border border-[#FF3B30]/20'
              }`}>
                <div className="flex items-center gap-3">
                  {importResult.imported_count > 0 ? (
                    <Check size={24} weight="bold" className="text-[#00E676]" />
                  ) : (
                    <Warning size={24} weight="bold" className="text-[#FF3B30]" />
                  )}
                  <div>
                    <p className={`font-medium ${
                      importResult.imported_count > 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                    }`}>
                      {importResult.imported_count > 0 
                        ? `Successfully imported ${importResult.imported_count} trades`
                        : 'No trades were imported'
                      }
                    </p>
                    {importResult.skipped_count > 0 && (
                      <p className="text-white/50 text-sm">
                        {importResult.skipped_count} rows were skipped
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                  <p className="text-white/40 text-xs font-mono mb-1">IMPORTED</p>
                  <p className="text-2xl font-bold text-[#00E676]">{importResult.imported_count}</p>
                </div>
                <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                  <p className="text-white/40 text-xs font-mono mb-1">SKIPPED</p>
                  <p className="text-2xl font-bold text-[#FF3B30]">{importResult.skipped_count}</p>
                </div>
              </div>

              {/* Skipped reasons */}
              {importResult.skipped_reasons?.length > 0 && (
                <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                  <p className="text-white/60 text-xs font-mono uppercase tracking-wider mb-2">Skipped reasons</p>
                  <ul className="text-white/50 text-xs space-y-1 max-h-32 overflow-y-auto">
                    {importResult.skipped_reasons.map((reason, i) => (
                      <li key={i}>• {reason}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Info about where data is used */}
              <div className="bg-[#00B8D4]/5 border border-[#00B8D4]/20 rounded-sm p-4">
                <p className="text-white/60 text-sm">
                  Your data has been successfully imported. You can view it in a separate Analytics section (Imported Analytics). This data is isolated and does not impact your core performance or insights.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          {step === 1 && (
            <Button
              variant="outline"
              onClick={handleClose}
              className="border-white/10 text-white/70 hover:bg-white/5"
            >
              Cancel
            </Button>
          )}
          
          {step === 2 && (
            <>
              <Button
                variant="outline"
                onClick={handleReset}
                className="border-white/10 text-white/70 hover:bg-white/5"
              >
                Back
              </Button>
              <Button
                onClick={handleImport}
                disabled={parsedRows.length === 0 || !importName.trim()}
                className="bg-[#00E676] text-black font-bold hover:bg-[#00E676]/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Import {parsedRows.length} trades
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </>
          )}
          
          {step === 4 && (
            <Button
              onClick={handleClose}
              className="bg-white/10 text-white hover:bg-white/15"
            >
              Done
            </Button>
          )}
        </div>
      </div>
      
      {/* Template Example Modal */}
      {showTemplate && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80"
          onClick={() => setShowTemplate(false)}
        >
          <div 
            className="bg-[#0A0A0A] border border-white/10 rounded-lg p-5 max-w-2xl w-full mx-4 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowTemplate(false)}
              className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors"
            >
              <X size={20} weight="bold" />
            </button>
            
            <p className="text-white/60 text-sm mb-4">
              CSV Template Example
            </p>
            
            <img
              src="/images/csv-template.png"
              alt="CSV Template"
              className="w-full rounded-md"
            />
            
            <p className="text-white/40 text-xs mt-4 text-center">
              Click outside or press X to close
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
